/**
 * Settings model: what the menus produce, and how it survives a reload.
 *
 * Three rules govern this file.
 *
 * NOTHING HERE REDEFINES A RANGE OR A DEFAULT. Camera limits come from
 * `CAMERA_SETTING_RANGES`, key defaults from `DEFAULT_BINDINGS`, match defaults
 * from `DEFAULT_MATCH_CONFIG`. A second copy of a default is a second thing to
 * forget to update.
 *
 * A BAD STORED VALUE MUST NEVER REACH THE SIMULATION AND MUST NEVER STOP THE
 * GAME BOOTING. localStorage is trivially writable from devtools, can hold a
 * half-finished write from a crashed tab, and in some privacy modes throws on
 * access rather than returning null. So every read goes through
 * `sanitizeSettings`, which is TOTAL: any input at all — `undefined`, a string,
 * an array, an object with the right keys and wrong types — yields a complete,
 * valid `GameSettings`. The only thing a caller ever has to handle is "these
 * are the defaults, not what you saved".
 *
 * OPTIONS THAT DO NOT WORK YET SAY SO. Every option table carries an
 * `implemented` flag that the menu renders as a visible lock, and nothing in
 * this file quietly stores a value the simulation will ignore without the UI
 * having said, in as many words, that it will be ignored. Bots and mutators are
 * wired now (see ./bots and src/physics/mutators.ts). The `learned` tier is the
 * one option whose availability is a RUNTIME fact rather than a build-time one:
 * it works exactly when a trained checkpoint exists, so its `implemented` flag
 * is computed — see `difficultyChoicesFor`.
 */

import { DEFAULT_MATCH_CONFIG, type MatchConfig } from './match';
import { legacyKeyFor, readMigrating } from './storage-migration';
import { MULTIPLAYER_MODES, modeSpec } from '../net/modes';
import type { MultiplayerMode } from '../net/protocol';
import { defaultServerAddress, normalizeServerUrl } from '../net/ws-transport';
import { DEFAULT_PPO_CONFIG } from '../rl/ppo';
// Type-only: src/rl/env pulls in the whole simulation, and this file is loaded
// by the menu long before a training run exists.
import type { ResetMode } from '../rl/env';
import { DEFAULT_BINDINGS, type KeyBindings } from '../input/controller';
import type { TouchMode } from '../input/touch-model';
import {
  BALL_BOUNCINESS_SCALE,
  BALL_SIZE_SCALE,
  defaultMutators,
  GAME_SPEED_SCALE,
  GRAVITY_SCALE,
  type BallBouncinessMutator,
  type BallSizeMutator,
  type BoostMutator,
  type GameSpeedMutator,
  type GravityMutator,
  type MutatorConfig,
} from '../physics/mutators';
import {
  clampCameraSettings,
  defaultCameraSettings,
  type CameraSettings,
} from '../render/camera';
import { CAR_BODY_SPECS, CAR_BODY_IDS, type CarBodyId } from '../render/car-body';

// ---------------------------------------------------------------------------
// Config shape
// ---------------------------------------------------------------------------

/** 0 = blue (defends -Y), 1 = orange. Same convention as everywhere else. */
export type TeamSide = 0 | 1;

export type GameMode =
  /** 1v1, human vs one bot. */
  | 'duel'
  /** 2v2, human plus an IDLE team-mate, against two bots. */
  | 'doubles'
  /** 2v2 where the team-mate is a bot too. */
  | 'doubles-bot-mate'
  /** Nobody drives; the local player watches two bot teams. */
  | 'bot-showcase'
  /**
   * A knockabout: one car, no opponent, an unlimited clock. The pitch, the
   * ball and the boost pads, with nothing trying to take them off you.
   */
  | 'freeplay';

/**
 * Mirrors `DifficultyTier` in src/bot/difficulty.ts, spelling included, so the
 * menu's value is handed to `BotDirector` verbatim by ./bots. Do not "tidy"
 * `allstar` into `all-star`.
 */
export type BotDifficulty = 'rookie' | 'pro' | 'allstar' | 'learned';

export type MatchLength = 'short' | 'standard' | 'unlimited';

/**
 * The mutator seam — now a real one.
 *
 * The enums, the `MutatorConfig` shape and the multiplier tables are DEFINED IN
 * THE CORE, at src/physics/mutators.ts, and re-exported here so the menus keep
 * importing them from the file they always did. Same rule as the rest of this
 * module: nothing here redefines a value that something else owns. The core
 * resolves a `MutatorConfig` into a `ResolvedPhysics` at world construction and
 * the simulation reads that; hand `World` this object and it plays by it.
 *
 * `gameSpeed` is the exception, and deliberately: the physics runs at a fixed
 * 120 Hz and the model is not dt-invariant, so game speed is applied by running
 * more or fewer ticks per real second — `TickPacer` in src/sim/loop.ts — not by
 * anything inside the world. See the header of src/physics/mutators.ts.
 */
export type {
  BallSizeMutator,
  BallBouncinessMutator,
  BoostMutator,
  GameSpeedMutator,
  GravityMutator,
  MutatorConfig,
};

/** Everything the match-setup screens produce, in one object. */
export interface MatchSetup {
  mode: GameMode;
  /**
   * Skill of every bot in the match, opponents and team-mate alike. Only the
   * tiers `DIFFICULTY_CHOICES` marks implemented can get here — see
   * `sanitizeMatchSetup`, which is what stops a hand-edited localStorage entry
   * asking for a tier that has no policy behind it.
   */
  difficulty: BotDifficulty;
  /** Which team the local player drives for; also drives the menu accent. */
  team: TeamSide;
  /**
   * The body the player's TEAM drives. The other team gets the other one — see
   * `carBodyForTeam`, which is the single place that rule lives.
   */
  car: CarBodyId;
  length: MatchLength;
  overtime: boolean;
  mutators: MutatorConfig;
}

/**
 * Who the learning agent trains against.
 *
 * Not part of `TrainingConfig` (src/rl/types.ts), and deliberately: the trainer
 * does not build environments, its ROLLOUT BACKEND does, so the opponent is a
 * property of the backend and is baked in when a run starts. The menu says as
 * much on the row — changing it mid-run would silently mean nothing.
 */
export type TrainingOpponent = 'self' | 'pool' | 'rookie' | 'pro' | 'allstar';

/** Everything the training screen's settings rows produce. */
export interface TrainingSettings {
  /** Web Workers collecting rollouts in parallel. */
  workers: number;
  /** Agent steps gathered per PPO update. */
  stepsPerIteration: number;
  learningRate: number;
  entropyCoef: number;
  /** Ticks before an episode is truncated. */
  episodeTicks: number;
  /** The initial-state distribution episodes are drawn from. See src/rl/env. */
  resetMode: ResetMode;
  opponent: TrainingOpponent;
  /** Mirror the newest policy into a live match while the run continues. */
  watchLive: boolean;
}

/**
 * Everything the multiplayer screens produce.
 *
 * Four fields, and every one of them is something the player typed or chose and
 * would be annoyed to retype. Nothing about a LIVE connection is here — the
 * room, the roster, the ping and the phase all belong to `NetClient` and none
 * of them survives a reload, so persisting any of them would only ever be a
 * stale lie on the next boot.
 *
 * `lastRoomCode` is the one that earns its keep: a private match is shared by
 * reading a five-character code aloud, and having to retype it after a reload
 * is exactly when a player gives up on the feature.
 */
export interface MultiplayerSettings {
  /** Where to connect. See `normalizeServerUrl` for what is accepted. */
  serverAddress: string;
  /** The last private room this client was in, for the Rejoin row. */
  lastRoomCode: string;
  /** Shown on every other player's scoreboard, so it is sanitized on the way in. */
  displayName: string;
  /** The quick-match mode last chosen. */
  mode: MultiplayerMode;
}

export interface GameSettings {
  /** Schema version of the STORED form; see `SETTINGS_VERSION`. */
  version: number;
  setup: MatchSetup;
  camera: CameraSettings;
  /** Ball cam on at kickoff. The in-match toggle lives on `InputController`. */
  ballCam: boolean;
  bindings: KeyBindings;
  training: TrainingSettings;
  multiplayer: MultiplayerSettings;
  audio: AudioSettings;
  touch: TouchSettings;
}

/**
 * The on-screen controls. Two questions, because they are independent: whether
 * to draw them at all, and whether the car drives without a button held.
 *
 * `mode` defaults to `auto`, which is the only value most players will ever
 * have — see `touchControlsVisible` in src/input/touch-model.ts for what auto
 * actually tests and why it is not simply "has a touchscreen". The other two
 * exist because that test cannot be right for everyone: `on` is how the layout
 * is checked on a desktop with a mouse, and `off` is for the tablet owner who
 * plays with a keyboard.
 */
export interface TouchSettings {
  mode: TouchMode;
  /**
   * Drive forward unless the brake is held. On by default, and the reason is
   * in the header of src/input/touch-model.ts: the right thumb cannot hold a
   * throttle and reach jump and boost at the same time.
   */
  autoThrottle: boolean;
}

/**
 * The two questions a player actually asks of game audio: how loud, and off.
 *
 * Separate rather than "volume 0 means muted", because they are separate
 * intentions — muting to take a call must not lose the level you had set, and
 * `Mixer` models it the same way for the same reason (see its header).
 */
export interface AudioSettings {
  /** Master level, 0..1. */
  volume: number;
  muted: boolean;
}

// ---------------------------------------------------------------------------
// Mutator payloads — the numbers the simulation applies
// ---------------------------------------------------------------------------

/**
 * Re-exported from src/physics/mutators.ts, which owns them. They are the
 * tables `resolvePhysics` itself reads, so a menu that renders `x1.5` and a
 * world that runs `x1.5` cannot disagree.
 *
 * These are GAME SETTINGS, not sourced physics constants, which is why they do
 * not live in src/physics/constants.ts — see that file's sourcing rule and the
 * header of src/physics/mutators.ts.
 */
export { BALL_SIZE_SCALE, BALL_BOUNCINESS_SCALE, GAME_SPEED_SCALE, GRAVITY_SCALE };

// ---------------------------------------------------------------------------
// Option tables
// ---------------------------------------------------------------------------

/**
 * One selectable value. `implemented: false` is not cosmetic — the menu refuses
 * to move off the current value on such a row and prints `note` next to it, so
 * the player is never shown a setting that silently does nothing.
 */
export interface Choice<T extends string | number | boolean> {
  value: T;
  label: string;
  /** Shown under the row while it is focused. */
  note?: string;
  implemented: boolean;
}

/** Cars per team for each mode. */
export const MODE_TEAM_SIZE: Record<GameMode, number> = {
  duel: 1,
  doubles: 2,
  'doubles-bot-mate': 2,
  'bot-showcase': 1,
  freeplay: 1,
};

/**
 * How many cars are actually on the pitch.
 *
 * `teamSize * 2` for every mode that has two sides, and 1 for free play —
 * which is the whole difference between free play and "a duel whose opponent
 * never moves". An idle car parked on the halfway line is something to bump
 * into and something to look at, and free play exists precisely so there is
 * neither. `World` takes an explicit roster for exactly this.
 */
export const carCountForMode = (mode: GameMode): number =>
  mode === 'freeplay' ? 1 : teamSizeForMode(mode) * 2;

/** True for the solo knockabout: no opponent, no clock pressure, no score. */
export const isFreePlay = (setup: MatchSetup): boolean => setup.mode === 'freeplay';

export const MODE_CHOICES: readonly Choice<GameMode>[] = [
  { value: 'duel', label: '1v1 Duel', implemented: true },
  {
    value: 'doubles',
    label: '2v2 Doubles',
    note: 'Your team-mate sits out; both opponents are bots.',
    implemented: true,
  },
  {
    value: 'doubles-bot-mate',
    label: '2v2 · Bot Team-mate',
    note: 'Three bots: two opponents and one on your side.',
    implemented: true,
  },
  {
    value: 'bot-showcase',
    label: 'Bot vs Bot · Spectate',
    note: 'Bots drive both cars. The camera follows your chosen team.',
    implemented: true,
  },
  {
    value: 'freeplay',
    label: 'Free Play',
    note: 'Just you, the ball and the boost pads. No opponent, no clock.',
    implemented: true,
  },
];

/**
 * The tiers, and which of them a match may actually be started with.
 *
 * `implemented` mirrors `DIFFICULTY_OPTIONS[].available` in
 * src/bot/difficulty.ts, which is the module that actually knows. `learned` is
 * false there because no trained policy ships, and `difficultyParams('learned')`
 * THROWS rather than quietly substituting a scripted bot — so the flag being
 * false here is not cosmetic. It is what keeps the menu from cycling onto a
 * value that would take the game down on Start Match, and `sanitizeMatchSetup`
 * enforces the same thing for stored values.
 *
 * THIS TABLE IS THE "NOTHING TRAINED YET" STATE. It is the base case, not the
 * whole story: once the player has trained a policy, a checkpoint exists and
 * the tier genuinely works. Everything that gates on the tier therefore takes a
 * `learnedAvailable` flag and goes through `difficultyChoicesFor` — a real
 * checkpoint is the only thing that flips it, and `BotConfig.policy` is the only
 * way the tier ever reaches `BotDirector`. The three refusals are unchanged when
 * that flag is false, which is the case this table describes.
 */

/** Note on the `learned` row while nothing has been trained. Phrased as a
 *  clause, because the menu prefixes it with the tier's label. */
export const LEARNED_LOCKED_NOTE =
  'no trained policy yet — train one on the Training screen.';

/** Note on the `learned` row once a checkpoint has made it selectable. */
export const LEARNED_UNLOCKED_NOTE = 'Every bot drives from your trained checkpoint.';

export const DIFFICULTY_CHOICES: readonly Choice<BotDifficulty>[] = [
  {
    value: 'rookie',
    label: 'Rookie',
    note: '0.1 s reaction, capped throttle, no aerials or wall reads.',
    implemented: true,
  },
  {
    value: 'pro',
    label: 'Pro',
    note: 'Aerials, wall reads and powerslides, with a little aim error.',
    implemented: true,
  },
  {
    value: 'allstar',
    label: 'All-Star',
    note: 'No reaction delay, no aim error, every mechanic unlocked.',
    implemented: true,
  },
  {
    value: 'learned',
    label: 'Learned (RL)',
    note: LEARNED_LOCKED_NOTE,
    implemented: false,
  },
];

/**
 * `DIFFICULTY_CHOICES`, with `learned` unlocked when a compatible checkpoint
 * exists. One function, so the cycler, the sanitizer and the row note cannot
 * disagree about whether the tier is available.
 *
 * The base table is returned UNCHANGED when nothing is trained, which is what
 * keeps the locked case exactly as it always was rather than a re-derivation of
 * it that could drift.
 */
export const difficultyChoicesFor = (
  learnedAvailable: boolean
): readonly Choice<BotDifficulty>[] =>
  learnedAvailable
    ? DIFFICULTY_CHOICES.map(c =>
        c.value === 'learned'
          ? { ...c, implemented: true, note: LEARNED_UNLOCKED_NOTE }
          : c
      )
    : DIFFICULTY_CHOICES;

/** Tiers a match can actually run with. Never empty. */
export const selectableDifficulties = (learnedAvailable: boolean): readonly BotDifficulty[] =>
  difficultyChoicesFor(learnedAvailable)
    .filter(c => c.implemented)
    .map(c => c.value);

/** The scripted tiers, i.e. what is selectable with nothing trained. */
export const SELECTABLE_DIFFICULTIES: readonly BotDifficulty[] = selectableDifficulties(false);

/**
 * The tier a match will really be played at. Anything unavailable — `'learned'`
 * whenever no checkpoint backs it — falls back to the first tier that exists, so
 * no path from a stored setting, a URL, or a future menu bug can reach
 * `difficultyParams` with a value that throws.
 *
 * The flag defaults to false on purpose: a caller that has not established that
 * a checkpoint exists gets the safe answer without having to know to ask for it.
 */
export const resolveDifficulty = (
  difficulty: BotDifficulty,
  learnedAvailable = false
): BotDifficulty => {
  const selectable = selectableDifficulties(learnedAvailable);
  return selectable.includes(difficulty) ? difficulty : (selectable[0] ?? 'pro');
};

export const TEAM_CHOICES: readonly Choice<TeamSide>[] = [
  { value: 0, label: 'Blue', implemented: true },
  { value: 1, label: 'Orange', implemented: true },
];

/**
 * The two bodies, built off the table src/render/car-body.ts owns rather than
 * re-listed here — the labels and the character notes belong next to the
 * geometry they describe, and a second copy is a second thing to forget.
 */
export type { CarBodyId };

export const CAR_CHOICES: readonly Choice<CarBodyId>[] = CAR_BODY_IDS.map(id => ({
  value: id,
  label: CAR_BODY_SPECS[id].label,
  note: CAR_BODY_SPECS[id].note,
  implemented: true,
}));

/**
 * Which body a team drives.
 *
 * The player's team drives the car the player chose and the OPPOSITION drives
 * the other one — always, in every mode. That is a deliberate choice rather
 * than "bots get a random car": with only two bodies, spending them on the two
 * teams means the silhouette carries the same information the paint does, and
 * a car half in shadow at the far end of the pitch is still identifiable as
 * yours or theirs. It also guarantees both bodies are on screen in every match,
 * which is the fastest way for a player to decide which one they want next.
 *
 * In `bot-showcase` nobody drives, but `setup.team` is still the side the
 * camera follows, so the rule needs no special case.
 */
export const otherCarBody = (car: CarBodyId): CarBodyId =>
  CAR_BODY_IDS.find(id => id !== car) ?? car;

export const carBodyForTeam = (setup: MatchSetup, team: TeamSide): CarBodyId =>
  team === setup.team ? setup.car : otherCarBody(setup.car);

/**
 * `MatchConfig` has no "no clock" flag and `src/game/match.ts` is not ours to
 * change, so "unlimited" is encoded as the longest duration `formatClock` can
 * render without going ragged: 99:59. The note on the row says so rather than
 * letting the label lie.
 */
export const UNLIMITED_MATCH_DURATION = 99 * 60 + 59;

export const MATCH_DURATIONS: Record<MatchLength, number> = {
  short: 180,
  standard: 300,
  unlimited: UNLIMITED_MATCH_DURATION,
};

export const LENGTH_CHOICES: readonly Choice<MatchLength>[] = [
  { value: 'standard', label: '5 Minutes', implemented: true },
  { value: 'short', label: '3 Minutes', implemented: true },
  {
    value: 'unlimited',
    label: 'Unlimited',
    note: 'Clock still counts, capped at 99:59 — the sim has no unclocked mode.',
    implemented: true,
  },
];

/**
 * The four rows below are `implemented: true`: `World` reads them, through
 * `resolvePhysics`, on every tick. What is still false is the MENU's own lock —
 * src/ui/menu-model.ts hard-codes `enabled: false` on every mutator row and has
 * a TODO to switch that to `choice.implemented`. Flipping it is that file's
 * one-line change; these flags are the signal it waits on.
 */
export const BALL_SIZE_CHOICES: readonly Choice<BallSizeMutator>[] = [
  { value: 'default', label: 'Default', implemented: true },
  { value: 'small', label: 'Small', implemented: true },
  { value: 'large', label: 'Large', implemented: true },
  { value: 'gigantic', label: 'Gigantic', implemented: true },
];

export const BALL_BOUNCINESS_CHOICES: readonly Choice<BallBouncinessMutator>[] = [
  { value: 'default', label: 'Default', implemented: true },
  { value: 'low', label: 'Low', implemented: true },
  { value: 'high', label: 'High', implemented: true },
  { value: 'super', label: 'Super High', implemented: true },
];

export const BOOST_CHOICES: readonly Choice<BoostMutator>[] = [
  { value: 'default', label: 'Default', implemented: true },
  { value: 'unlimited', label: 'Unlimited', implemented: true },
  { value: 'none', label: 'No Boost', implemented: true },
  { value: 'recharge', label: 'Recharge', implemented: true },
];

/**
 * Still locked, and for a different reason from the rest: the SIMULATION cannot
 * implement game speed at all, because the physics is a fixed 120 Hz and the
 * model is not dt-invariant. It is a driver setting — `TickPacer` in
 * src/sim/loop.ts converts a frame time into a tick count at this scale — so
 * these rows unlock when whoever owns the frame loop uses it, not when the
 * core changes. See the header of src/physics/mutators.ts.
 */
export const GAME_SPEED_CHOICES: readonly Choice<GameSpeedMutator>[] = [
  { value: 'default', label: 'Default', implemented: false, note: 'Needs the frame loop, not the physics.' },
  { value: 'slow', label: 'Slo-Mo', implemented: false, note: 'Needs the frame loop, not the physics.' },
  { value: 'fast', label: 'Time Warp', implemented: false, note: 'Needs the frame loop, not the physics.' },
];

export const GRAVITY_CHOICES: readonly Choice<GravityMutator>[] = [
  { value: 'default', label: 'Default', implemented: true },
  { value: 'low', label: 'Low', implemented: true },
  { value: 'high', label: 'High', implemented: true },
  { value: 'reverse', label: 'Reverse', implemented: true },
];

// ---------------------------------------------------------------------------
// Training option tables
// ---------------------------------------------------------------------------

/**
 * Every training row is a CHOICE, not a free slider, and every one of them is
 * `implemented: true`.
 *
 * A hyperparameter typed in freehand is a hyperparameter that can be typed
 * wrong: a learning rate of 3 rather than 3e-4 does not fail, it produces a
 * run that looks like it is training and is in fact diverging, and the player
 * has no way to tell. A short list of values that are known to be in range
 * removes that failure entirely, and costs nothing anybody would miss — the
 * interesting decisions here are all order-of-magnitude ones.
 */
const numberChoices = (
  values: readonly number[],
  label: (v: number) => string
): readonly Choice<number>[] => values.map(v => ({ value: v, label: label(v), implemented: true }));

/** Capped at 8: past a machine's core count the workers time-slice, and the
 *  UI thread is one of the things they take slices from. */
export const WORKER_CHOICES: readonly Choice<number>[] = numberChoices(
  [1, 2, 3, 4, 6, 8],
  v => `${v}`
);

export const STEPS_PER_ITERATION_CHOICES: readonly Choice<number>[] = numberChoices(
  [1024, 2048, 4096, 8192, 16384],
  v => v.toLocaleString('en-US')
);

/** Written in exponent form because that is the form these are reasoned about
 *  in, and because 0.0003 and 0.003 are one glance apart in decimal. */
export const LEARNING_RATE_CHOICES: readonly Choice<number>[] = numberChoices(
  [1e-4, 3e-4, 1e-3, 3e-3],
  v => v.toExponential(0).replace('e-', 'e−')
);

export const ENTROPY_COEF_CHOICES: readonly Choice<number>[] = numberChoices(
  [0, 0.001, 0.005, 0.01, 0.02, 0.05],
  v => v.toFixed(3)
);

/** Labelled in seconds at the simulation's fixed 120 Hz, which is the unit the
 *  number actually means to anybody watching a run. */
export const EPISODE_TICKS_CHOICES: readonly Choice<number>[] = numberChoices(
  [600, 1200, 2400, 4800],
  v => `${(v / 120).toFixed(0)} s`
);

/**
 * Where an episode starts.
 *
 * Ordered easiest-first, and `curriculum` is first because it is the only one
 * that a policy with no ball contact yet can climb out of: from a full-size
 * reset a fresh network essentially never reaches the ball, so the touch rate
 * and every reward term that depends on it stay at zero for as long as anyone
 * watches. See src/rl/env.
 */
export const RESET_MODE_CHOICES: readonly Choice<ResetMode>[] = [
  {
    value: 'curriculum',
    label: 'Curriculum',
    note: 'Starts beside the ball and backs off as the touch rate rises. The one to leave alone.',
    implemented: true,
  },
  {
    value: 'nearBall',
    label: 'Near Ball',
    note: 'Always beside the ball, at the difficulty the curriculum has reached.',
    implemented: true,
  },
  {
    value: 'kickoff',
    label: 'Kickoff',
    note: 'Standard kickoffs only. A policy trained on these has seen nothing else.',
    implemented: true,
  },
  {
    value: 'random',
    label: 'Random',
    note: 'Ball and cars anywhere legal. Full difficulty from iteration one.',
    implemented: true,
  },
  { value: 'mixed', label: 'Kickoff + Random', implemented: true },
];

export const OPPONENT_CHOICES: readonly Choice<TrainingOpponent>[] = [
  {
    value: 'self',
    label: 'Self-Play',
    note: 'Both cars learn from one network. Strongest ceiling, slowest start.',
    implemented: true,
  },
  {
    value: 'pool',
    label: 'Self-Play · Frozen Pool',
    note: 'Learns against snapshots of its own past. Steadier than pure self-play.',
    implemented: true,
  },
  {
    value: 'rookie',
    label: 'vs Rookie',
    note: 'A scripted opponent gives a policy something to beat from iteration one.',
    implemented: true,
  },
  { value: 'pro', label: 'vs Pro', implemented: true },
  { value: 'allstar', label: 'vs All-Star', implemented: true },
];

/** Menu row descriptors for the training screen's settings, in display order. */
export const TRAINING_ROWS: readonly {
  key: keyof TrainingSettings;
  label: string;
  choices: readonly Choice<string | number>[];
  /** Shown under the row when the row's own choice has nothing to say. */
  note?: string;
}[] = [
  {
    key: 'workers',
    label: 'Workers',
    choices: WORKER_CHOICES,
    note: 'Web Workers collecting rollouts. Applied when a run starts.',
  },
  {
    key: 'stepsPerIteration',
    label: 'Steps / Iteration',
    choices: STEPS_PER_ITERATION_CHOICES,
    // The honest version of "bigger is better": the PPO update runs on the main
    // thread, and its cost is linear in this. 16,384 is a three-second freeze
    // every iteration — measurably, on this machine — and there is no way to
    // find that out from the number itself.
    note: 'Agent steps per PPO update. Steadier when bigger, but the update runs on this thread and freezes it for longer.',
  },
  { key: 'learningRate', label: 'Learning Rate', choices: LEARNING_RATE_CHOICES },
  {
    key: 'entropyCoef',
    label: 'Entropy Coef',
    choices: ENTROPY_COEF_CHOICES,
    note: 'The lever against premature collapse. Watch the entropy chart.',
  },
  {
    key: 'episodeTicks',
    label: 'Episode Length',
    choices: EPISODE_TICKS_CHOICES,
    note: 'Truncation point. Shorter episodes mean more independent starts.',
  },
  {
    key: 'resetMode',
    label: 'Episode Start',
    choices: RESET_MODE_CHOICES,
    note: 'Where episodes begin. Baked into the rollout workers when a run starts.',
  },
  {
    key: 'opponent',
    label: 'Opponent',
    choices: OPPONENT_CHOICES,
    note: 'Baked into the rollout workers when a run starts.',
  },
];

// ---------------------------------------------------------------------------
// Multiplayer option tables
// ---------------------------------------------------------------------------

/**
 * The modes the quick-match screen offers, in menu order.
 *
 * Derived from `MODE_RULES` (src/net/modes.ts) rather than re-listed, for the
 * reason that file states: the lobby, the matchmaker and the room must size a
 * match identically, and three copies of "duel means one per team" is how a
 * queue forms a room the server then refuses to start. `private` is excluded
 * because it is not a queue destination — it is what Host Private makes.
 *
 * Every one is `implemented: true`: the server has all six today, and a mode
 * this table could not honour would have to be locked rather than hidden.
 */
export const MULTIPLAYER_MODE_CHOICES: readonly Choice<MultiplayerMode>[] = MULTIPLAYER_MODES.filter(
  mode => mode !== 'private'
).map(mode => {
  const spec = modeSpec(mode);
  return {
    value: mode,
    label: spec.label,
    note:
      mode === 'freeplay'
        ? 'No teams and no clock. Everyone in one arena, knocking the ball about.'
        : spec.fillWithBots
          ? `${spec.teamSize}v${spec.teamSize}. Empty seats are filled with bots so the match starts.`
          : `${spec.teamSize} per team.`,
    implemented: true,
  };
});

/** Cars per team for a multiplayer mode. One source, shared with the server. */
export const multiplayerTeamSize = (mode: MultiplayerMode): number => modeSpec(mode).teamSize;

/** The full spec — team size, bot fill and label — for a mode. */
export const multiplayerModeSpec = modeSpec;

/** Longest name the scoreboard has room for. Matches the server's own cap. */
export const MAX_DISPLAY_NAME = 24;

/** Room codes as server/rooms.ts writes them, and as `isJoinCode` checks them. */
const ROOM_CODE_PATTERN = /^[A-Z0-9]{4,8}$/;

/**
 * A typed room code, normalized.
 *
 * Upper-cased and stripped rather than rejected: the code is read aloud and
 * typed back in, so `abc12` and `ABC 12` are the same code as far as the player
 * is concerned, and refusing them would be the menu being right and useless.
 * Anything that still does not fit the server's shape becomes '' — the empty
 * string is what the Rejoin row reads as "nothing to rejoin".
 */
export const sanitizeRoomCode = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return ROOM_CODE_PATTERN.test(cleaned) ? cleaned : '';
};

export const isRoomCode = (value: string): boolean => ROOM_CODE_PATTERN.test(value);

/**
 * Control and format characters: a hole in somebody else's scoreboard layout,
 * and a forged second line in a log file. `sanitizeName` in
 * src/net/server-room.ts strips the same class on arrival.
 *
 * Written as the Unicode "Other" category rather than as an explicit range, so
 * this source file contains no literal control characters of its own — and so
 * zero-width joiners and bidi overrides, which are not C0/C1 but ruin a name
 * column just as thoroughly, go with them.
 */
const UNPRINTABLE = /\p{C}/gu;

/**
 * A display name, made safe for somebody else's scoreboard.
 *
 * The same rule `sanitizeName` in src/net/server-room.ts applies, repeated here
 * rather than imported: that module pulls in `World` and a `BotDirector`, and
 * this file is loaded by the menu long before a match exists. The server
 * sanitizes again on arrival regardless — this copy exists so the name in the
 * text field is the name that will appear, not a longer one that gets cut.
 */
export const sanitizeDisplayName = (value: unknown): string => {
  if (typeof value !== 'string') return DEFAULT_DISPLAY_NAME;
  const cleaned = value.replace(UNPRINTABLE, '').trim().slice(0, MAX_DISPLAY_NAME);
  return cleaned.length === 0 ? DEFAULT_DISPLAY_NAME : cleaned;
};

export const DEFAULT_DISPLAY_NAME = 'Player';

/**
 * A server address, or the default.
 *
 * Validated through the same normalizer the transport uses, so an address the
 * menu accepts is an address that will actually connect — a field that stores
 * `htp://localhot` and only fails three screens later is the shape of this bug.
 */
export const sanitizeServerAddress = (value: unknown): string => {
  if (typeof value !== 'string') return defaultServerAddress();
  return normalizeServerUrl(value) ?? defaultServerAddress();
};

export const defaultMultiplayerSettings = (): MultiplayerSettings => ({
  serverAddress: defaultServerAddress(),
  lastRoomCode: '',
  displayName: DEFAULT_DISPLAY_NAME,
  mode: 'duel',
});

export const sanitizeMultiplayerSettings = (value: unknown): MultiplayerSettings => {
  const base = defaultMultiplayerSettings();
  if (!isRecord(value)) return base;
  return {
    serverAddress: sanitizeServerAddress(value.serverAddress),
    lastRoomCode: sanitizeRoomCode(value.lastRoomCode),
    displayName: sanitizeDisplayName(value.displayName),
    mode: oneOf(value.mode, MULTIPLAYER_MODE_CHOICES, base.mode),
  };
};

/**
 * Total, like every sanitizer here: any shape in, usable settings out.
 *
 * A volume outside 0..1 is clamped rather than rejected — a hand-edited 5 is a
 * clear intention to be loud, and the mixer would clamp it anyway. `muted` is
 * strict-true so any other value reads as "not muted"; starting silent because
 * of a junk payload is the one failure the player cannot diagnose by ear.
 */
export const sanitizeAudioSettings = (value: unknown): AudioSettings => {
  if (!isRecord(value)) return defaultAudioSettings();
  const volume =
    typeof value.volume === 'number' && Number.isFinite(value.volume)
      ? Math.max(0, Math.min(1, value.volume))
      : DEFAULT_AUDIO_VOLUME;
  return { volume, muted: value.muted === true };
};

// ---------------------------------------------------------------------------
// Touch controls
// ---------------------------------------------------------------------------

export const TOUCH_MODE_CHOICES: readonly Choice<TouchMode>[] = [
  {
    value: 'auto',
    label: 'Automatic',
    note: 'Shown when the primary pointer is a finger — a phone or a tablet.',
    implemented: true,
  },
  {
    value: 'on',
    label: 'Always On',
    note: 'Drawn on every device. The stick works with a mouse.',
    implemented: true,
  },
  {
    value: 'off',
    label: 'Off',
    note: 'Never drawn. A tablet with a keyboard or a pad wants this.',
    implemented: true,
  },
];

export const AUTO_THROTTLE_CHOICES: readonly Choice<boolean>[] = [
  {
    value: true,
    label: 'Auto',
    note: 'The car drives forward on its own; Brake reverses.',
    implemented: true,
  },
  {
    value: false,
    label: 'Manual',
    note: 'Adds a Drive button. One thumb, four buttons — see how it feels.',
    implemented: true,
  },
];

export const defaultTouchSettings = (): TouchSettings => ({
  mode: 'auto',
  autoThrottle: true,
});

/**
 * Total, like every sanitizer here. An unknown `mode` falls back to `auto`
 * rather than to `off`: a stored value this function does not recognise is a
 * corrupt payload, not a preference, and the failure it must not produce is a
 * phone with no controls at all and no way to reach the setting that fixes it.
 */
export const sanitizeTouchSettings = (value: unknown): TouchSettings => {
  if (!isRecord(value)) return defaultTouchSettings();
  const mode = TOUCH_MODE_CHOICES.some(c => c.value === value.mode)
    ? (value.mode as TouchMode)
    : 'auto';
  return { mode, autoThrottle: value.autoThrottle !== false };
};

/** Menu row descriptors for the mutator screen, in display order. */
export const MUTATOR_ROWS: readonly {
  key: keyof MutatorConfig;
  label: string;
  choices: readonly Choice<string>[];
}[] = [
  { key: 'ballSize', label: 'Ball Size', choices: BALL_SIZE_CHOICES },
  { key: 'ballBounciness', label: 'Ball Bounciness', choices: BALL_BOUNCINESS_CHOICES },
  { key: 'boost', label: 'Boost', choices: BOOST_CHOICES },
  { key: 'gameSpeed', label: 'Game Speed', choices: GAME_SPEED_CHOICES },
  { key: 'gravity', label: 'Gravity', choices: GRAVITY_CHOICES },
];

// ---------------------------------------------------------------------------
// Derived match configuration
// ---------------------------------------------------------------------------

export const teamSizeForMode = (mode: GameMode): number =>
  MODE_TEAM_SIZE[mode] ?? MODE_TEAM_SIZE.duel;

/**
 * Index into `World.cars` of the car the local player drives. `World` fills the
 * roster team-0-first, so orange starts at `teamSize`. Returns 0 for spectate
 * modes as well — the camera has to follow *something* — but see
 * `isSpectator`, which is what decides whether input reaches that car.
 */
export const playerCarIndex = (setup: MatchSetup): number =>
  // Free play has ONE car whichever colour it is painted, so orange does not
  // shift the index the way it does in a two-sided mode — there is no team 0
  // block in front of it to skip past.
  setup.mode === 'freeplay' ? 0 : setup.team === 1 ? teamSizeForMode(setup.mode) : 0;

export const isSpectator = (setup: MatchSetup): boolean => setup.mode === 'bot-showcase';

/**
 * The `MatchConfig` a setup implies. Everything not on a menu keeps its default.
 *
 * Free play overrides the length and overtime rows rather than reading them:
 * the mode's promise is that nothing ends, and honouring a stored "3 Minutes"
 * from the last duel would break it three minutes in.
 */
export const matchConfigFor = (setup: MatchSetup): MatchConfig => ({
  ...DEFAULT_MATCH_CONFIG,
  duration: isFreePlay(setup)
    ? UNLIMITED_MATCH_DURATION
    : (MATCH_DURATIONS[setup.length] ?? MATCH_DURATIONS.standard),
  overtimeEnabled: isFreePlay(setup) ? false : setup.overtime,
});

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Bumped only when a stored payload can no longer be read field-for-field.
 * v1 is the first shipped schema, so there is nothing behind it to migrate
 * from — see `migrateStored` for where a v1 -> v2 step would go.
 *
 * `training` was ADDED to v1 rather than opening a v2, and that is the rule
 * this constant encodes rather than an oversight: a purely additive field needs
 * no migration, because `sanitizeSettings` is total and fills a missing one with
 * its default. Bumping instead would make every existing player's camera, key
 * bindings and match setup vanish to buy nothing — `migrateStored` discards a
 * payload whose version does not match, by design. The version moves when a
 * field CHANGES MEANING, which is the case that cannot be repaired by
 * sanitizing.
 */
export const SETTINGS_VERSION = 1;

/** Stable across versions; the version lives inside the payload, not in the key. */
export const SETTINGS_STORAGE_KEY = 'vroomball.settings';

/** Matches `InputController.ballCam`, which also starts on. */
export const DEFAULT_BALL_CAM = true;

/** Re-exported from the core, which owns the default the simulation resolves
 *  against; a second copy here is a second thing to forget to update. */
export { defaultMutators };

export const defaultMatchSetup = (): MatchSetup => ({
  mode: 'duel',
  difficulty: 'pro',
  team: 0,
  car: 'octane',
  length: 'standard',
  overtime: DEFAULT_MATCH_CONFIG.overtimeEnabled,
  mutators: defaultMutators(),
});

export const BINDABLE_ACTIONS = Object.keys(DEFAULT_BINDINGS) as (keyof KeyBindings)[];

/** How many keys one action may be bound to. Matches the controls screen. */
export const MAX_BINDING_SLOTS = 2;

export const defaultBindings = (): KeyBindings => cloneBindings(DEFAULT_BINDINGS);

/**
 * Training defaults, tuned for a BROWSER TAB rather than for a long CLI run.
 *
 * The learning rate and entropy coefficient come from `DEFAULT_PPO_CONFIG`,
 * which owns them. `stepsPerIteration` does not, and the reason is the one
 * thing measurement made obvious: THE ROLLOUTS ARE NOT THE EXPENSIVE HALF.
 *
 * Measured in Chrome on a 32-core desktop, at this project's default network
 * (two hidden layers of 256, four PPO epochs), with the rollouts in four Web
 * Workers. Approximate, and machine-dependent, but the SHAPE is the point:
 *
 *   steps/iteration   rollout (workers)   PPO update (main thread)
 *   1,024             ~0.2 s              ~1-2 s
 *   2,048             ~0.3 s              ~4 s
 *   4,096             ~0.5 s              ~8-11 s
 *
 * The workers do their job — a collection is a fraction of an iteration. The
 * update is what freezes the page, and it grows WORSE than linearly, so the
 * smallest batch PPO's 256-sample minibatches still divide sensibly is the
 * right default. Everything else here follows from the same budget: a
 * ten-second episode so a batch spans many independent starts, and four workers
 * so a collection finishes well inside one update.
 *
 * The training screen shows both halves of that split live, under `Rollout` and
 * `UI Freeze`, because a number this large should not be a comment nobody
 * reads.
 */
export const defaultTrainingSettings = (): TrainingSettings => ({
  workers: 4,
  stepsPerIteration: 1024,
  learningRate: DEFAULT_PPO_CONFIG.learningRate,
  entropyCoef: DEFAULT_PPO_CONFIG.entropyCoef,
  episodeTicks: 1200,
  // The curriculum, for the reason `RESET_MODE_CHOICES` gives: without it the
  // touch rate never leaves zero and the charts on this screen have nothing to
  // show.
  resetMode: 'curriculum',
  // A scripted opponent, not self-play: a fresh network against a fresh network
  // is two cars doing nothing, and the first useful signal — the touch rate —
  // takes far longer to appear than it does against something that moves.
  opponent: 'rookie',
  watchLive: true,
});

/**
 * Loud enough to be the game's voice, quiet enough not to be the first thing a
 * player reaches for the slider over. Not muted: a silent default would leave
 * every sound in the game undiscovered.
 */
export const DEFAULT_AUDIO_VOLUME = 0.7;

export const defaultAudioSettings = (): AudioSettings => ({
  volume: DEFAULT_AUDIO_VOLUME,
  muted: false,
});

export const defaultSettings = (): GameSettings => ({
  version: SETTINGS_VERSION,
  setup: defaultMatchSetup(),
  camera: defaultCameraSettings(),
  ballCam: DEFAULT_BALL_CAM,
  bindings: defaultBindings(),
  training: defaultTrainingSettings(),
  multiplayer: defaultMultiplayerSettings(),
  audio: defaultAudioSettings(),
  touch: defaultTouchSettings(),
});

/**
 * Deep copy of a binding map. Every path that hands bindings to something that
 * might mutate them goes through this: `DEFAULT_BINDINGS` is a module-level
 * literal, and one in-place `push` onto it would corrupt the defaults for the
 * rest of the session, including "reset to defaults".
 */
export function cloneBindings(source: KeyBindings): KeyBindings {
  const out = {} as KeyBindings;
  for (const action of BINDABLE_ACTIONS) out[action] = [...source[action]];
  return out;
}

// ---------------------------------------------------------------------------
// Sanitizing
// ---------------------------------------------------------------------------

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Pick `value` if it is one of `choices`, else `fallback`. */
const oneOf = <T extends string | number>(
  value: unknown,
  choices: readonly Choice<T>[],
  fallback: T
): T => (choices.some(c => c.value === value) ? (value as T) : fallback);

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const sanitizeMutators = (value: unknown): MutatorConfig => {
  const base = defaultMutators();
  if (!isRecord(value)) return base;
  // Written through a widened alias: every row's value type differs, but each
  // one is validated against its own choice list, so the write is sound.
  const out = base as unknown as Record<string, string>;
  for (const row of MUTATOR_ROWS) {
    out[row.key] = oneOf(value[row.key], row.choices, base[row.key]);
  }
  return base;
};

/**
 * Training settings, validated row by row against the same tables the menu
 * cycles. Anything not on a list falls back to the default rather than being
 * clamped into range: an out-of-list learning rate is not a value that was
 * nearly right, it is a value nothing chose.
 */
export const sanitizeTrainingSettings = (value: unknown): TrainingSettings => {
  const base = defaultTrainingSettings();
  if (!isRecord(value)) return base;
  // Written through a widened alias for the same reason `sanitizeMutators` is:
  // each row's value type differs, and each is checked against its own list.
  const out = base as unknown as Record<string, string | number>;
  for (const row of TRAINING_ROWS) {
    out[row.key] = oneOf(value[row.key], row.choices, out[row.key]);
  }
  base.watchLive = bool(value.watchLive, base.watchLive);
  return base;
};

/**
 * `learnedAvailable` says whether a compatible checkpoint exists. It defaults to
 * false so that a caller which has not looked cannot accidentally admit the
 * tier: the answer is asynchronous (IndexedDB), and "not yet known" and "not
 * there" must resolve the same way.
 */
export const sanitizeMatchSetup = (value: unknown, learnedAvailable = false): MatchSetup => {
  const base = defaultMatchSetup();
  if (!isRecord(value)) return base;
  return {
    mode: oneOf(value.mode, MODE_CHOICES, base.mode),
    // Validated against the AVAILABLE tiers only, unlike every other row: a
    // stored `learned` with nothing trained would otherwise reach
    // `difficultyParams`, which throws. localStorage is trivially writable, so
    // this is a real path, not a hypothetical one.
    difficulty: resolveDifficulty(
      oneOf(value.difficulty, DIFFICULTY_CHOICES, base.difficulty),
      learnedAvailable
    ),
    team: oneOf(value.team, TEAM_CHOICES, base.team),
    car: oneOf(value.car, CAR_CHOICES, base.car),
    length: oneOf(value.length, LENGTH_CHOICES, base.length),
    overtime: bool(value.overtime, base.overtime),
    mutators: sanitizeMutators(value.mutators),
  };
};

/**
 * A stored key list is kept only if it is an array of non-empty strings.
 * Anything else falls back to that action's default rather than to "unbound",
 * because an action with no keys is indistinguishable to the player from a
 * broken keyboard. An explicitly emptied slot list is preserved, though: the
 * controls screen lets you clear a binding on purpose.
 */
const sanitizeKeyList = (value: unknown, fallback: string[]): string[] => {
  if (!Array.isArray(value)) return [...fallback];
  const codes = value.filter(
    (c): c is string => typeof c === 'string' && c.length > 0 && c.length < 64
  );
  // Duplicates inside one action would double-count nothing but would render
  // as two identical key caps, so collapse them here.
  return [...new Set(codes)].slice(0, MAX_BINDING_SLOTS);
};

export const sanitizeBindings = (value: unknown): KeyBindings => {
  const base = defaultBindings();
  if (!isRecord(value)) return base;
  for (const action of BINDABLE_ACTIONS) {
    base[action] = sanitizeKeyList(value[action], base[action]);
  }
  return base;
};

/**
 * Total: returns a complete, valid `GameSettings` for literally any input.
 * Camera values additionally go through `clampCameraSettings`, so a stored FOV
 * of 4000 or of `"wide"` lands inside `CAMERA_SETTING_RANGES` instead of
 * blanking the screen with a NaN projection matrix.
 */
export const sanitizeSettings = (value: unknown, learnedAvailable = false): GameSettings => {
  if (!isRecord(value)) return defaultSettings();
  return {
    version: SETTINGS_VERSION,
    setup: sanitizeMatchSetup(value.setup, learnedAvailable),
    camera: clampCameraSettings(
      isRecord(value.camera) ? (value.camera as Partial<CameraSettings>) : {}
    ),
    ballCam: bool(value.ballCam, DEFAULT_BALL_CAM),
    bindings: sanitizeBindings(value.bindings),
    training: sanitizeTrainingSettings(value.training),
    // Additive, exactly like `training` was: a payload written before
    // multiplayer existed has no such key, `sanitizeMultiplayerSettings` is
    // total, and the player keeps their camera and bindings. That is why
    // SETTINGS_VERSION does not move — see the note on it.
    multiplayer: sanitizeMultiplayerSettings(value.multiplayer),
    // Additive in exactly the same way, and for the same reason the version
    // does not move: a payload written before the game had sound has no such
    // key, the sanitizer is total, and the player keeps everything else.
    audio: sanitizeAudioSettings(value.audio),
    // And again for the on-screen controls. `auto` is what a payload without
    // the key resolves to, which is what every player had before it existed.
    touch: sanitizeTouchSettings(value.touch),
  };
};

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/** The slice of the Storage API we use; lets tests pass a plain object. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type LoadStatus =
  /** Read and used as-is. */
  | 'ok'
  /** No stored payload at all — first run, or cleared. */
  | 'missing'
  /** Present but not parseable / not an object. */
  | 'corrupt'
  /** Parseable but written by a different schema version. */
  | 'stale'
  /** Storage itself threw or is unavailable (private mode, disabled). */
  | 'unavailable';

export interface LoadResult {
  settings: GameSettings;
  status: LoadStatus;
}

/**
 * `learnedAvailable` is threaded all the way through the write path as well as
 * the read path, and it has to be: without it, saving a setup whose tier is
 * `learned` would sanitize the tier back out on the way to storage, and the
 * player's own selection would silently revert the next time anything at all
 * changed. The gate is against a value that has NO checkpoint behind it, not
 * against the value itself.
 */
export const serializeSettings = (settings: GameSettings, learnedAvailable = false): string =>
  JSON.stringify(sanitizeSettings(settings, learnedAvailable));

/**
 * Version handling. An older payload is dropped wholesale rather than
 * partially salvaged: v1 is the first schema, so "older" can only mean
 * something hand-written or from a pre-release build, and neither is worth
 * trusting field by field.
 *
 * TODO(schema): when v2 lands, add a `case 1:` here that upgrades the v1 shape
 * and returns status 'ok'. `sanitizeSettings` will still run afterwards, so a
 * migration only has to move fields, not validate them.
 */
const migrateStored = (parsed: unknown, learnedAvailable: boolean): LoadResult => {
  if (!isRecord(parsed)) return { settings: defaultSettings(), status: 'corrupt' };
  if (parsed.version !== SETTINGS_VERSION) {
    return { settings: defaultSettings(), status: 'stale' };
  }
  return { settings: sanitizeSettings(parsed, learnedAvailable), status: 'ok' };
};

/** Parse a raw stored string. Never throws; `null` means "nothing stored". */
export const parseSettings = (raw: string | null, learnedAvailable = false): LoadResult => {
  if (raw === null || raw === '') return { settings: defaultSettings(), status: 'missing' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { settings: defaultSettings(), status: 'corrupt' };
  }
  return migrateStored(parsed, learnedAvailable);
};

/**
 * Resolved lazily, never at module scope: importing this file in Node, in a
 * worker, or in a locked-down iframe must not touch `localStorage`.
 */
export const browserStorage = (): StorageLike | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

export const loadSettings = (
  storage: StorageLike | null = browserStorage(),
  learnedAvailable = false
): LoadResult => {
  if (!storage) return { settings: defaultSettings(), status: 'unavailable' };
  let raw: string | null;
  try {
    raw = readMigrating(storage, SETTINGS_STORAGE_KEY, legacyKeyFor(SETTINGS_STORAGE_KEY));
  } catch {
    // Safari in private mode used to throw on read; some extensions still do.
    return { settings: defaultSettings(), status: 'unavailable' };
  }
  return parseSettings(raw, learnedAvailable);
};

/** True if the write landed. A failed save is never worth an exception. */
export const saveSettings = (
  settings: GameSettings,
  storage: StorageLike | null = browserStorage(),
  learnedAvailable = false
): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(SETTINGS_STORAGE_KEY, serializeSettings(settings, learnedAvailable));
    return true;
  } catch {
    // QuotaExceededError, or storage disabled between boot and now.
    return false;
  }
};

export const clearSettings = (storage: StorageLike | null = browserStorage()): void => {
  if (!storage) return;
  try {
    storage.removeItem(SETTINGS_STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
};
